import React, { useState } from 'react';
import { X, Eye, EyeOff, Loader2 } from 'lucide-react';
import { loginadmin } from '../../assets/assets';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useSetupHellocianPassword } from '../../services/auth/auth';
import { useAlert } from '../../context/AlertContext';

const SetupPassword = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const setAlert = useAlert();
    const { mutateAsync: setupPassword, isPending } = useSetupHellocianPassword();

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    const togglePasswordVisibility = (field) => {
        if (field === 'password') {
            setShowPassword(prev => !prev);
        } else {
            setShowConfirmPassword(prev => !prev);
        }
    };

    const validatePassword = (password) => {
        const passwordRegex = /^(?=(?:[^A-Z]*[A-Z]){3})(?=(?:[^a-z]*[a-z]){2})(?=(?:\D*\d){2})(?=.*[^a-zA-Z0-9]).{8,}$/;
        return passwordRegex.test(password);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validatePassword(formData.password)) {
            setError('Password must be at least 8 characters long, contain at least 3 uppercase letters, 2 lowercase letters, 2 digits, and 1 special character.');
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        try {
            await setupPassword({
                token,
                password: formData.password
            });
            setAlert({ type: 'success', message: 'Password set successfully! You can now log in.' });
            navigate('/clients/signin'); // Or a unified signin if available
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to set password. Link may be invalid or expired.');
        }
    };

    return (
        <div className="font-['Poppins'] bg-gray-50 min-h-screen flex md:flex-row-reverse flex-col">
            {/* Image section */}
            <div className="w-[88%] mt-[4rem] md:mt-0 mx-auto md:w-1/2 md:max-h-screen">
                <Link to="/" className="absolute top-4 right-4 z-10 md:hidden">
                    <X size={24} className="text-gray-800" />
                </Link>

                <img
                    src={loginadmin}
                    alt="Hellocian Setup"
                    className="w-full h-64 md:h-full object-cover"
                />
            </div>

            {/* Form section */}
            <div className="md:w-1/2 px-6 py-8 md:px-16 md:py-12 md:flex md:flex-col md:justify-center">
                <div className="max-w-md w-full mx-auto">
                    <h1 className="text-2xl font-bold text-[#174568] mb-2">Set Your Password</h1>
                    <p className="text-gray-600 mb-8">Welcome to Hellocer! Complete your account setup by choosing a secure password.</p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="password" className="block text-gray-700 mb-2 font-medium">New Password</label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Enter your password"
                                    className="w-full p-3 border-x-0 border-t-0 border-b border-[#C1C7CD] bg-[#F2F4F8] focus:outline-none focus:border-b-[#174568] transition-all"
                                    required
                                    disabled={isPending}
                                />
                                <button
                                    type="button"
                                    onClick={() => togglePasswordVisibility('password')}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-gray-700 mb-2 font-medium">Confirm Password</label>
                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="Confirm your password"
                                    className="w-full p-3 border-x-0 border-t-0 border-b border-[#C1C7CD] bg-[#F2F4F8] focus:outline-none focus:border-b-[#174568] transition-all"
                                    required
                                    disabled={isPending}
                                />
                                <button
                                    type="button"
                                    onClick={() => togglePasswordVisibility('confirm')}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                                Password must be at least 8 characters long, contain at least 3 uppercase letters, 2 lowercase letters, 2 digits, and 1 special character.
                            </p>
                        </div>

                        {error && (
                            <div className="bg-red-50 border-l-4 border-red-500 p-4 text-red-700 text-sm">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isPending}
                            className="w-full bg-[#174568] hover:bg-[#0f3a4f] disabled:bg-gray-400 text-white font-semibold py-4 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                        >
                            {isPending ? <Loader2 className="animate-spin" size={24} /> : 'Complete Setup'}
                        </button>
                    </form>

                    <div className="text-center mt-8">
                        <Link to="/clients/signin" className="text-[#174568] hover:underline font-medium">
                            Already have an account? Sign in
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SetupPassword;
